import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle as ApproveIcon, ArrowBack as BackIcon } from '@mui/icons-material';

const OfflinePaymentApproval = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [password, setPassword] = useState('');

  // Get parameters from URL
  const appId = searchParams.get('appid');
  const paymentType = searchParams.get('payType');
  const loginName = searchParams.get('app');

  // Decode the base64 encoded parameters
  const decodedAppId = appId ? atob(appId) : '';
  const decodedPaymentType = paymentType ? atob(paymentType) : '';
  const decodedLoginName = loginName ? atob(loginName) : '';

  useEffect(() => {
    // Validate that all required parameters are present
    if (!appId || !paymentType || !loginName) {
      setError('Missing required parameters. Please navigate from the offline payment details page.');
    }
  }, [appId, paymentType, loginName]);

  const handleApproval = async () => {
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // First validate the password
      const passwordResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/accounts/validate-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            password,
            orgId: localStorage.getItem('orgId') || '1'
          })
        }
      );

      const passwordResult = await passwordResponse.json();

      if (!passwordResult.success) {
        setError('Invalid Password');
        return;
      }

      // If password is valid, proceed with approval
      const approvalResponse = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/accounts/approve-offline-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            appId: decodedAppId,
            paymentTypeId: decodedPaymentType,
            loginName: decodedLoginName,
            orgId: localStorage.getItem('orgId') || '1'
          })
        }
      );

      const approvalResult = await approvalResponse.json();

      if (approvalResult.success) {
        setSuccess('Payment has been approved successfully!');
        setTimeout(() => {
          // Redirect back to offline payment details page
          navigate('/main/accounts/offline-payment-details');
        }, 2000);
      } else {
        setError(approvalResult.message || 'Failed to approve payment');
      }

    } catch (error) {
      setError('Error approving payment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/main/accounts/offline-payment-details');
  };

  if (!appId || !paymentType || !loginName) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Invalid access. Please navigate from the offline payment details page.
        </Alert>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={handleCancel}
          sx={{ mt: 2 }}
        >
          Back to Offline Payment Details
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#00024c' }}>
        Offline Payment Approval
      </Typography>

      {/* Back Button */}
      <Button
        variant="outlined"
        startIcon={<BackIcon />}
        onClick={handleCancel}
        sx={{ mb: 3 }}
      >
        Back to Payment Details
      </Button>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Payment Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Payment Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Application ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {decodedAppId}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Login Name:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {decodedLoginName}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Type ID:
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {decodedPaymentType}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status:
                  </Typography>
                  <Chip label="Pending Approval" color="warning" size="small" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Approval Action */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Approve Payment
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please enter your password to approve this offline payment.
              </Typography>
              
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                sx={{ mb: 2 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleApproval();
                  }
                }}
              />
              
              <Button
                fullWidth
                variant="contained"
                startIcon={loading ? <CircularProgress size={16} /> : <ApproveIcon />}
                onClick={handleApproval}
                disabled={loading || !password.trim()}
                sx={{ mb: 1 }}
              >
                {loading ? 'Approving...' : 'Approve Payment'}
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OfflinePaymentApproval;